import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";

// Move MULTIPLE layers to a new position in the tree as one atomic step,
// preserving their relative (document) order.
//
// The naive approach — moving each dragged layer one at a time with a separate
// ReorderLayerCommand while re-resolving the drop target's live index each
// iteration — inverts the dragged layers' relative order when dropping toward
// the top of the stack (audit i117), and produces N history entries instead of
// one. This command removes all dragged ids at once, then re-inserts them as a
// contiguous block at the target position in their original relative order, in
// a single undoable step.
//
// - Dragged ids that are groups move as a whole subtree; if the set contains a
//   group AND one of its own descendants, only the group moves (the descendant
//   rides along inside it) — no double-move.
// - `targetId` must not be one of the dragged ids (the caller filters it out);
//   if it is missing / among the dragged after removal, the command is a no-op.
export class ReorderLayersCommand implements Command {
  readonly kind = "reorder-layers";
  private before: readonly Layer[] | null = null;

  constructor(
    private readonly layerIds: readonly string[],
    private readonly targetId: string,
  ) {}

  apply(doc: Document): Document {
    const idSet = new Set(this.layerIds);
    if (idSet.size === 0 || idSet.has(this.targetId)) return doc;

    const block: Layer[] = [];
    collectInOrder(doc.layers, idSet, block);
    if (block.length === 0) return doc;

    const reduced = removeIds(doc.layers, idSet);
    const pos = findPosition(reduced, this.targetId, null);
    if (!pos) return doc; // target vanished (shouldn't happen — it isn't dragged)

    this.before = doc.layers;
    const inserted = insertBlockAt(reduced, block, pos.parentId, pos.index);
    return { ...doc, layers: inserted };
  }

  invert(before: Document, _after: Document): Command {
    const snapshot = this.before ?? before.layers;
    const self = this;
    return {
      kind: "reorder-layers-restore",
      apply: (d: Document): Document => ({ ...d, layers: snapshot }),
      invert: (): Command => self, // redo re-applies the block move
    };
  }
}

interface Pos { parentId: string | null; index: number; }

/** Collect dragged layers/groups in DFS document order; a matched group is
 *  pushed whole and NOT descended into (so its own matched descendants aren't
 *  double-collected). */
function collectInOrder(layers: readonly Layer[], idSet: ReadonlySet<string>, out: Layer[]): void {
  for (const l of layers) {
    if (idSet.has(l.id)) { out.push(l); continue; }
    if (l.kind === "group") collectInOrder(l.children, idSet, out);
  }
}

function removeIds(layers: readonly Layer[], idSet: ReadonlySet<string>): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (idSet.has(l.id)) continue;
    if (l.kind === "group") out.push({ ...l, children: removeIds(l.children, idSet) } as GroupLayer);
    else out.push(l);
  }
  return out;
}

function findPosition(layers: readonly Layer[], id: string, parentId: string | null): Pos | null {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === id) return { parentId, index: i };
    if (l.kind === "group") {
      const inner = findPosition(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

function insertBlockAt(layers: readonly Layer[], block: readonly Layer[], parentId: string | null, index: number): Layer[] {
  if (parentId === null) {
    const clamped = Math.max(0, Math.min(index, layers.length));
    return [...layers.slice(0, clamped), ...block, ...layers.slice(clamped)];
  }
  return layers.map((l) => {
    if (l.id === parentId && l.kind === "group") {
      const clamped = Math.max(0, Math.min(index, l.children.length));
      return { ...l, children: [...l.children.slice(0, clamped), ...block, ...l.children.slice(clamped)] } as GroupLayer;
    }
    if (l.kind === "group") return { ...l, children: insertBlockAt(l.children, block, parentId, index) } as GroupLayer;
    return l;
  });
}
